<script setup lang="ts">
import type { CrossValidationReportDto } from "@/dto";

const { report } = defineProps<{
  report: CrossValidationReportDto;
}>();

const selectedSplitIndex = defineModel<number>("selectedSplitIndex", { required: true });

function selectSplit(splitIndex: number) {
  if (splitIndex !== selectedSplitIndex.value) {
    selectedSplitIndex.value = splitIndex;
  } else {
    selectedSplitIndex.value = -1;
  }
}
</script>

<template>
  <div class="report-summary-split-selector">
    <header class="text-base-medium">
      <i class="icon icon-database" />
      Dataset overview
    </header>
    <div class="selected-split">
      Viewing
      <span v-if="selectedSplitIndex !== -1" class="split-index">
        split {{ selectedSplitIndex + 1 }} of {{ report.splitting_strategy.splits.length }}.
      </span>
      <span v-else class="split-index">all splits.</span>
    </div>
    <div class="selector" :class="{ 'no-selection': selectedSplitIndex === -1 }">
      <div
        v-for="i in report.splitting_strategy.splits.length"
        :key="i"
        class="split"
        :class="{ selected: i - 1 === selectedSplitIndex }"
        @click="selectSplit(i - 1)"
      />
    </div>
  </div>
</template>

<style scoped>
.report-summary-split-selector {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-16);

  header {
    .icon {
      color: var(--border-secondary);
      vertical-align: text-bottom;
    }
  }

  .selected-split {
    .split-index {
      color: var(--content-brand);
    }
  }

  .selector {
    display: flex;
    flex-flow: row wrap;

    .split {
      width: var(--numbers-12);
      height: var(--numbers-12);
      box-sizing: content-box;
      border: solid var(--border-tertiary) var(--stroke-width-md);
      background-color: var(--background-tertiary);
      cursor: pointer;
      transition:
        background-color var(--animation-duration) var(--animation-easing),
        border-color var(--animation-duration) var(--animation-easing);

      &.selected {
        background-color: var(--background-brand);
      }

      &:hover {
        background-color: var(--background-tertiary-hover);
      }

      &:hover.selected {
        background-color: var(--background-brand-hover);
      }

      &:active {
        background-color: var(--background-tertiary-pressed);
      }
    }

    &.no-selection {
      .split {
        border-color: var(--border-info);
        background-color: var(--background-brand);

        &:hover {
          background-color: var(--background-brand-hover);
        }

        &:active {
          background-color: var(--background-brand-pressed);
        }
      }
    }
  }
}
</style>
